import { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import * as yup from 'yup'

import { transLang } from 'shared/locale'
import { MIN_PASS_LENGTH } from 'shared/utils/forms'
import { IUserSignUp } from 'shared/utils/types/Auth.type'

import { signIn, signUp } from 'features/auth/actions'
import { selectAdditionalData, selectAuthCommunication } from 'features/auth/selectors'
import { getCurrenciesList } from 'features/settings/actions'
import { selectCurrenciesList } from 'features/settings/selectors'
import { selectLanguage } from 'features/userSettings/selectors'

import { SelectOption } from 'components/UIKit/Select/Select'
import { getSelectOptionValue } from 'components/UIKit/Select/utils'

export type UseSignUpFormProps = {
  onSuccessCallback?: () => void
}

const validationSchema = yup.object().shape({
  email: yup.string().required('required').email('enterValidEmail'),
  repeatEmail: yup
    .string()
    .required('required')
    .email('enterValidEmail')
    .oneOf([yup.ref('email'), ''], 'emailsDoNotMatch'),
  username: yup.string().required('required'),
  password: yup.string().min(MIN_PASS_LENGTH, 'minPassLength').required('required'),
  repeatPassword: yup
    .string()
    .required('required')
    .oneOf([yup.ref('password'), ''], 'passwordsDoNotMatch'),
  currency: yup.mixed().required('required'),
})

type SignUpFormT = {
  email: string
  repeatEmail: string
  username: string
  password: string
  repeatPassword: string
  isAdult: boolean
  iAccept: boolean
  currency: SelectOption | string
}

const mockRegistrationInfo = {
  country: 'TN',
  mobile_number: '0',
}

const SIGN_UP_DEFAULT_CURRENCY = 'TND'

export const useSignUpForm = ({ onSuccessCallback }: UseSignUpFormProps) => {
  const dispatch = useDispatch()
  const lang = useSelector(selectLanguage, shallowEqual)
  const additionalData = useSelector(selectAdditionalData, shallowEqual)
  const currencies = useSelector(selectCurrenciesList, shallowEqual)

  useEffect(() => {
    dispatch(getCurrenciesList())
  }, [dispatch])

  const currencyOptions = useMemo<SelectOption[]>(() => currencies.map(currency => ({ value: currency })), [currencies])

  const formik = useFormik<SignUpFormT>({
    initialValues: {
      email: '',
      repeatEmail: '',
      username: '',
      password: '',
      repeatPassword: '',
      isAdult: false,
      iAccept: false,
      currency: SIGN_UP_DEFAULT_CURRENCY,
    },
    validationSchema,
    onSubmit: values => {
      const userData: IUserSignUp = {
        email: values.email,
        username: values.username,
        password1: values.password,
        password2: values.repeatPassword,
        currency: getSelectOptionValue(values.currency) ?? SIGN_UP_DEFAULT_CURRENCY,
        info: mockRegistrationInfo,
        language: transLang[lang],
        ...(additionalData?.click_id
          ? {
              affise_data: {
                click_id: String(additionalData.click_id),
                pid: Number(additionalData?.pid),
              },
            }
          : {}),
        ...(additionalData?.ref_id
          ? {
              pap_data: {
                ref_id: additionalData.ref_id ? String(additionalData.ref_id) : undefined,
                visitor_id: additionalData?.visitor_id ? String(additionalData?.visitor_id) : undefined,
                banner_id: additionalData?.banner_id ? String(additionalData?.banner_id) : undefined,
              },
            }
          : {}),
      }

      dispatch(
        signUp(
          { ...userData },
          {
            callback: () => {
              dispatch(
                signIn({ username: userData.username, password: userData.password1 }, { callback: onSuccessCallback }),
              )
            },
          },
        ),
      )
    },
  })

  const { isLoading, isSuccess, error } = useSelector(selectAuthCommunication, shallowEqual).signUp

  return {
    formik,
    currencyOptions,
    isLoading,
    isSuccess,
    error,
  }
}
